<script lang="ts" setup>
import AppConfigurator from './AppConfigurator.vue'
import AppBackButton from './AppBackButton.vue'
import AppBreadcrumb from './AppBreadcrumb.vue'
import AppNotificationsMenu from '~/components/layout/AppNotificationsMenu.vue'
import AppIncomingCalls from '~/components/layout/AppIncomingCalls.vue'

const { hasWorkspaceAccess, canUsePhone } = useAuth()
const { currentOrganization: organization } = useOrganization()
const { state: dialerState, timer } = useDialer()
const { toggleMenu } = useTemplateLayout()
const { darkMode, savingAppearance, toggleDarkMode } = useAppearance()
const { siteName } = useSite()
</script>

<template>
  <div class="layout-topbar">
    <div class="layout-topbar-logo-container">
      <button
        aria-label="Toggle navigation"
        class="layout-menu-button layout-topbar-action"
        @click="toggleMenu"
      >
        <i class="pi pi-bars"></i>
      </button>
      <router-link to="/" class="layout-topbar-logo" :class="{ 'sos-logo-tile': !organization?.logo }">
        <img
          :src="organization?.logo || '/images/sos-restoration.webp'"
          :alt="organization?.name || siteName"
          :class="{ 'sos-nav-logo--default': !organization?.logo }"
        />
      </router-link>
    </div>

    <AppBackButton />
    <AppBreadcrumb class="max-[420px]:sr-only" />
    <div class="layout-topbar-actions">
      <div class="layout-config-menu">
        <Button
          v-if="
            organization &&
            canUsePhone
          "
          :label="timer > 0 ? formatDuration(timer) : undefined"
          :severity="timer > 0 ? 'primary' : 'contrast'"
          :variant="timer > 0 ? undefined : 'text'"
          aria-label="Open dialer"
          icon="pi pi-phone"
          rounded
          @click="dialerState.isOpen = !dialerState.isOpen"
        />

        <AppNotificationsMenu v-if="organization" :organization />
        <AppIncomingCalls v-if="organization && hasWorkspaceAccess('marketing')" />
      </div>

      <button
        aria-label="Account and appearance"
        class="layout-topbar-menu-button layout-topbar-action"
        v-styleclass="{
          selector: '@next',
          enterFromClass: 'hidden',
          enterActiveClass: 'animate-scalein',
          leaveToClass: 'hidden',
          leaveActiveClass: 'animate-fadeout',
          hideOnOutsideClick: true,
        }"
      >
        <i class="pi pi-ellipsis-v"></i>
      </button>

      <div class="layout-topbar-menu hidden lg:block">
        <div class="layout-topbar-menu-content">
          <button
            type="button"
            class="layout-topbar-action"
            aria-label="Dark mode"
            :aria-pressed="darkMode"
            :disabled="savingAppearance"
            @click="toggleDarkMode"
          >
            <i
              :class="[
                'pi',
                {
                  'pi-moon': darkMode,
                  'pi-sun': !darkMode,
                },
              ]"
            ></i>
            <span class="lg:hidden">Dark mode</span>
          </button>

          <div class="relative">
            <button
              v-styleclass="{
                selector: '@next',
                enterFromClass: 'hidden',
                enterActiveClass: 'animate-scalein',
                leaveToClass: 'hidden',
                leaveActiveClass: 'animate-fadeout',
                hideOnOutsideClick: true,
              }"
              type="button"
              aria-label="Color palette"
              class="layout-topbar-action layout-topbar-action-highlight"
            >
              <i class="pi pi-palette"></i>
              <span class="lg:hidden">Palette</span>
            </button>
            <AppConfigurator />
          </div>

          <button
            type="button"
            aria-label="Profile"
            class="layout-topbar-action"
            @click="() => navigateTo('/account')"
          >
            <i class="pi pi-user"></i>
            <span>Profile</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 420px) {
  .layout-topbar .layout-topbar-logo-container {
    flex-shrink: 1;
    min-width: 0;
  }
  .layout-menu-button {
    flex-shrink: 0;
  }
  .layout-topbar-logo {
    min-width: 0;
  }
  .layout-topbar .layout-topbar-logo img {
    max-width: min(100%, 5rem);
  }
}
</style>
